import { PLACEMENTS } from "../../constants/ads";

const defaultState = {
  // timeline posts
  data: [],
  hasMore: true,
  pagingToken: null,
  appliedFilters: {
    category: null,
  },
  // show modal for post hidden by default
  isPostModalOpen: false,
  current: {
    // data for post modal (post object)
    comments: {
      data: [],
      pagingToken: null,
      hasMore: false,
      isFetching: false,
      pending: [],
    },
    similarPosts: {
      posts: [],
      isFetching: false,
      pagingToken: null,
      hasMore: false,
    },
    isRatingPending: false,
    userRating: 0,
    isLoading: false,
  },
  deleting: false,
  //
  // opened profile posts
  openedProfileUserPosts: {
    hasMore: true,
    data: [],
    allDeleted: false,
  },
  topPosts: [],
};

const Posts = (state = defaultState, action) => {
  switch (action.type) {
    case "LOAD_MORE_POSTS_SUCCESS": {
      const { override = false, limit, posts: fetchedPosts, pagingToken } = action;
      const hasMore = !(fetchedPosts.length < limit);
      if (override) {
        return { ...state, data: [...fetchedPosts], hasMore, pagingToken };
      }
      return { ...state, data: [...state.data, ...fetchedPosts], hasMore, pagingToken };
    }

    case "CLEAR_POSTS":
      return {
        ...state,
        data: [],
        hasMore: true,
        pagingToken: null,
      };

    case "SHOW_POST_MODAL": {
      return {
        ...state,
        current: {
          ...state.current,
          ...action.post,
        },
        isPostModalOpen: true,
      };
    }

    case "HIDE_POST_MODAL": {
      return {
        ...state,
        current: defaultState.current,
        isPostModalOpen: false,
      };
    }

    case "LOAD_MORE_OPENED_PROFILE_POSTS_SUCCESS": {
      const { limit, posts: fetchedPosts, pagingToken } = action;
      const hasMore = !(fetchedPosts.length < limit);
      return {
        ...state,
        openedProfileUserPosts: {
          ...state.openedProfileUserPosts,
          data: action.override? fetchedPosts: [...state.openedProfileUserPosts.data, ...fetchedPosts],
          hasMore,
          pagingToken,
        },
      };
    }

    case "CLEAN_OPENED_PROFILE_POSTS":
      return {
        ...state,
        openedProfileUserPosts: {
          hasMore: true,
          data: [],
          allDeleted: false,
        },
      };

    case "DELETE_POST_OPTIMISTIC":
      const profilePosts = removePost(state.openedProfileUserPosts.data, action.postId);
      const feedPosts = removePost(state.data, action.postId);
      return {
        ...state,
        data: feedPosts.posts,
        openedProfileUserPosts: {
          ...state.openedProfileUserPosts,
          data: profilePosts.posts,
          allDeleted: profilePosts.allDeleted,
        },
      };

    case "DELETE_POST_FAILED":
      return {
        ...state,
        data: restorePost(state.data, action.postId),
        openedProfileUserPosts: {
          ...state.openedProfileUserPosts,
          data: restorePost(state.openedProfileUserPosts.data, action.postId),
          //@NOTE: doesn't seem very useful
          allDeleted: false,
        },
      };

    case "HIDE_POST_OPTIMISTIC":
      return setPostHiddenState(state, action, true);

    case "HIDE_POST_FAILED":
      return setPostHiddenState(state, action, false);

    case "UNHIDE_POST_OPTIMISTIC":
      return setPostHiddenState(state, action, false);

    case "UNHIDE_POST_FAILED":
      return setPostHiddenState(state, action, true);

    case "SET_JUST_PUBLISHED":
      return {
        ...state,
        current: {
          ...state.current,
          isJustPublished: true,
        },
      };

    case "CLEAR_JUST_PUBLISHED":
      return {
        ...state,
        current: {
          ...state.current,
          isJustPublished: false,
        },
      };

    case "LIKE_POST_OPTIMISTIC":
      return setPostLike(state, action, true);

    case "UNLIKE_POST_OPTIMISTIC":
      return setPostLike(state, action, false);

    case "LOAD_CURRENT_POST_BY_ID_REQUEST":
    case "LOAD_CURRENT_POST_BY_SLUG_REQUEST": {
      return {
        ...state,
        current: {
          ...state.current,
          isLoading: true,
        },
      };
    }

    case "LOAD_CURRENT_POST_SUCCESS":
      return {
        ...state,
        current: {
          ...state.current,
          ...action.post,
          isJustPublished: state.current.isJustPublished,
          isLoading: false,
        },
      };

    case "CLEAR_CURRENT_POST":
      return {
        ...state,
        current: {
          ...defaultState.current,
        },
      };

    case "LOAD_POST_COMMENTS_SUCCESS": {
      const { limit, comments: fetchedComments, pagingToken } = action;
      const hasMore = !(fetchedComments.length < limit);
      return {
        ...state,
        current: {
          ...state.current,
          comments: {
            ...state.current.comments,
            data: [...fetchedComments, ...state.current.comments.data],
            hasMore,
            pagingToken,
          },
        },
      };
    }

    case "SET_CURRENT_POST_COMMENTS_FETCHING_STATUS":
      return {
        ...state,
        current: {
          ...state.current,
          comments: {
            ...state.current.comments,
            isFetching: action.fetchingStatus,
          },
        },
      };

    case "SET_EDIT_COMMENT_MODE":
      return {
        ...state,
        current: {
          ...state.current,
          comments: {
            ...state.current.comments,
            data: state.current.comments.data.map(
              comment => (comment._id === action.commentId ? { ...comment, isInEditMode: action.isEditMode } : comment)
            ),
          },
        },
      };
    case "DELETE_COMMENT_OPTIMISTIC":
      return {
        ...state,
        current: {
          ...state.current,
          comments: {
            ...state.current.comments,
            data: state.current.comments.data.map(
              comment => (comment._id === action.commentId ? { ...comment, isDeleted: true } : comment)
            ),
          },
        },
      };

    case "DELETE_COMMENT_FAILED":
      return {
        ...state,
        current: {
          ...state.current,
          comments: {
            ...state.current.comments,
            data: state.current.comments.data.map(
              comment => (comment._id === action.commentId ? { ...comment, isDeleted: false } : comment)
            ),
          },
        },
      };

    case "EDIT_COMMENT_OPTIMISTIC":
      return {
        ...state,
        current: {
          ...state.current,
          comments: {
            ...state.current.comments,
            data: state.current.comments.data.map(
              comment =>
                comment._id === action.commentId
                  ? {
                      ...comment,
                      content: action.content,
                      previouseContent: comment.content,
                    }
                  : comment
            ),
          },
        },
      };

    case "EDIT_COMMENT_FAILED":
      return {
        ...state,
        current: {
          ...state.current,
          comments: {
            ...state.current.comments,
            data: state.current.comments.data.map(
              comment => (comment._id === action.commentId ? { ...comment, content: action.previouseContent } : comment)
            ),
          },
        },
      };

    case "CREATE_COMMENT_OPTIMISTIC":
      if (state.current._id === action.postId) {
        return {
          ...state,
          current: {
            ...state.current,
            comments: {
              ...state.current.comments,
              pending: [...state.current.comments.pending, action.comment],
            },
          },
        };
      }
      return state;

    case "CREATE_COMMENT_SUCCESS":
      if (state.current._id === action.postId) {
        return {
          ...state,
          current: {
            ...state.current,
            comments: {
              ...state.current.comments,
              data: [...state.current.comments.data, action.newComment],
              pending: state.current.comments.pending.filter(comment => comment._id !== action.pendingCommentId),
            },
          },
        };
      }
      return state;

    case "CREATE_COMMENT_FAILED":
      if (state.current._id === action.postId) {
        return {
          ...state,
          current: {
            ...state.current,
            comments: {
              ...state.current.comments,
              pending: state.current.comments.pending.filter(comment => comment._id !== action.pendingCommentId),
            },
          },
        };
      }
      return state;

    case "INJECT_ADS_IN_FEED":
      const placements = [...PLACEMENTS];
      const ads = [...action.ads];
      const postsWithAds = state.data.filter(post => !post.sponsered).reduce((newPosts, post, index) => {
        if (placements.length > 0 && placements[0] === index) {
          const ad = ads.shift() || { sponsered: true };
          placements.shift();
          return newPosts.concat([ad, post]);
        }
        return newPosts.concat([post]);
      }, []);
      return {
        ...state,
        data: postsWithAds,
      };

    case "SET_POST_CATEGORY_FILTER":
      return {
        ...state,
        appliedFilters: {
          ...state.appliedFilters,
          category: action.categoryId,
        },
      };

    case "CLEAR_POST_CATEGORY_FILTER":
      return {
        ...state,
        appliedFilters: {
          ...state.appliedFilters,
          category: null,
        },
      };

    case "LOAD_SIMILAR_POSTS_REQUEST":
      return {
        ...state,
        current: {
          ...state.current,
          similarPosts: {
            ...state.current.similarPosts,
            isFetching: true,
          },
        },
      };

    case "LOAD_SIMILAR_POSTS_SUCCESS":
      const { limit = 3, similarPosts: fetchedPosts, pagingToken } = action;
      const hasMore = !(fetchedPosts.length < limit);
      return {
        ...state,
        current: {
          ...state.current,
          similarPosts: {
            ...state.current.similarPosts,
            isFetching: false,
            posts: [...state.current.similarPosts.posts, ...fetchedPosts],
            hasMore,
            pagingToken,
          },
        },
      };
    case "UNFOLLOW_USER_FAILED":
    case "FOLLOW_USER_OPTIMISTIC":
      if (action.postId === state.current._id) {
        return {
          ...state,
          current: {
            ...state.current,
            owner: {
              ...state.current.owner,
              isFollowed: true,
            },
          },
        };
      }
      return state;

    case "UNFOLLOW_USER_OPTIMISTIC":
    case "FOLLOW_USER_FAILED":
      if (action.postId === state.current._id) {
        return {
          ...state,
          current: {
            ...state.current,
            owner: {
              ...state.current.owner,
              isFollowed: false,
            },
          },
        };
      }
      return state;

    case "RATE_POST_REQUEST":
      return {
        ...state,
        current: {
          ...state.current,
          isRatingPending: true,
          userRating: action.rating,
        },
      };

    case "RATE_POST_FAILED":
      return {
        ...state,
        current: {
          ...state.current,
          isRatingPending: false,
          userRating: 0,
        },
      };

    case "RATE_POST_SUCCESS":
      return {
        ...state,
        current: {
          ...state.current,
          isRatingPending: false,
          isJustRated: true,
        },
      };

    case "LOAD_TOP_POSTS_SUCCESS":
      return {
        ...state,
        topPosts: [...action.posts],
      };

    default:
      return state;
  }
};

function removePost(postArray, postId) {
  let allDeleted = true;
  const posts = postArray.map(post => {
    if (post._id === postId) {
      return {
        ...post,
        deleted: true,
      };
    } else if (!post.deleted) {
      allDeleted = false;
    }
    return { ...post };
  });

  return {
    posts,
    allDeleted,
  };
}

function restorePost(postArray, postId) {
  return postArray.map(post => {
    if (post._id === postId) {
      return {
        ...post,
        deleted: false,
      };
    }
    return { ...post };
  });
}

function setPostLike(state, action, isLiked) {
  const numberChange = isLiked ? 1 : -1;
  const newState = {
    data: state.data.map(post => {
      // Prevent the post from being liked twice in the feed
      if (post._id === action.postId && !(isLiked && post.isLiked)) {
        if (!post.interactions) {
          post.interactions = { likesCount: 0 };
        }
        return {
          ...post,
          isLiked,
          interactions: {
            ...post.interactions,
            likesCount: post.interactions.likesCount + numberChange,
          },
        };
      }
      return post;
    }),
    openedProfileUserPosts: {
      ...state.openedProfileUserPosts,
      data: state.openedProfileUserPosts.data.map(post => {
        if (post._id === action.postId) {
          if (!post.interactions) {
            post.interactions = { likesCount: 0 };
          }
          return {
            ...post,
            isLiked,
            interactions: {
              ...post.interactions,
              likesCount: post.interactions.likesCount + numberChange,
            },
          };
        }
        return post;
      }),
    },
  };
  if (state.current._id === action.postId) {
    if (!state.current.interactions) {
      state.current.interactions = { likesCount: 0 };
    }
    newState.current = {
      ...state.current,
      isLiked,
      interactions: {
        ...state.current.interactions,
        likesCount: state.current.interactions.likesCount + numberChange,
      },
    };
  }
  return {
    ...state,
    ...newState,
  };
}

function setPostHiddenState(state, action, isHidden) {
  return {
    ...state,
    data: state.data.map(post => {
      if (post._id === action.postId) {
        return {
          ...post,
          isHidden, // To animate the card
          isActive: !isHidden, // To update the options dropdown
        };
      }
      return post;
    }),
    openedProfileUserPosts: {
      ...state.openedProfileUserPosts,
      data: state.openedProfileUserPosts.data.map(post => {
        if (post._id === action.postId) {
          return {
            ...post,
            isActive: !isHidden, // To update the options dropdown
          };
        }
        return post;
      }),
    },
    current: {
      ...state.current,
      isActive: state.current._id === action.postId ? !isHidden : state.current.isActive,
    },
  };
}

export default Posts;



// WEBPACK FOOTER //
// ./src/reducers/Posts/index.js